import { Textarea } from "@do/ui/components/textarea";
import { cn } from "@do/ui/lib/utils";
import { useTranslations } from "next-intl";
import React, { useCallback, useEffect, useMemo } from "react";
import { useChat } from "../../../providers/chat-provider";

type FooterInputTextAreaProps = {
  focusPreviewInput?: boolean;
  message: string;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  handleSubmitMessage: (e?: React.FormEvent<HTMLFormElement>) => void;
};

export const FooterInputTextArea = ({
  focusPreviewInput,
  message,
  setMessage,
  handleSubmitMessage,
}: FooterInputTextAreaProps) => {
  const {
    inputRef,
    maxUserMessageLength,
    readonlyModeMessage,
    chatbotParams,
    selectedLocale,
    defaultLocale,
    disabled,
  } = useChat();
  const t = useTranslations("do-chat-v2");

  const placeholder = useMemo(() => {
    return selectedLocale?.placeholder || defaultLocale?.placeholder || chatbotParams.placeholder;
  }, [selectedLocale, defaultLocale, chatbotParams.placeholder]);

  const hintId = "footer-input-textarea-hint";

  // Safari does not yet properly support `field-sizing: content`.
  // So we force autosizing in JS by adjusting the height on each message change.
  useEffect(() => {
    const textarea = inputRef?.current;
    if (!textarea) return;

    // We reset the height to "auto" so it can shrink when needed,
    textarea.style.height = "auto";
    // then, we set it to scrollHeight (letting CSS handle min/max-height).
    textarea.style.height = `${textarea.scrollHeight + 2}px`; // 2px for the border width
  }, [inputRef, message]);

  const handleChangeMessage = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value.slice(0, maxUserMessageLength));
    },
    [maxUserMessageLength, setMessage]
  );

  const handleKeyDownMessage = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (readonlyModeMessage) return;
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmitMessage();
      }
    },
    [handleSubmitMessage, readonlyModeMessage]
  );

  const handleFocusMessage = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Prevent mobile browsers from scrolling the page when the keyboard opens,
    // which can fight our own scroll logic and dismiss the keyboard immediately.
    e.target.scrollIntoView = (_arg?: boolean | ScrollIntoViewOptions) => {};
  }, []);

  return (
    <>
      <Textarea
        id="footer-input-textarea"
        aria-label={t("chat.footer.accessibility.input-label")}
        aria-describedby={hintId}
        ref={inputRef}
        disabled={disabled}
        readOnly={!!readonlyModeMessage}
        aria-readonly={!!readonlyModeMessage}
        autoComplete="off"
        rows={1}
        maxLength={maxUserMessageLength}
        value={readonlyModeMessage || message}
        onChange={handleChangeMessage}
        onFocus={handleFocusMessage}
        onKeyDown={handleKeyDownMessage}
        placeholder={placeholder}
        className={cn(
          "resize-none",
          "min-h-(--footer-input-min-height)",
          "max-h-(--footer-input-max-height)",
          "text-(length:--footer-input-font-size)",
          "font-(family-name:--footer-input-font-family)",
          "placeholder:text-(--footer-input-placeholder-color)/(--footer-input-placeholder-color-opacity)",
          "rounded-(--footer-input-border-radius)",
          "border-(--footer-input-border-color)/(--footer-input-border-opacity)",
          "bg-(--footer-input-background-color)/(--footer-input-background-opacity)",
          "text-(--footer-input-foreground)/(--footer-input-foreground-opacity)",
          "px-(--footer-input-padding-x) pr-(--footer-input-padding-right)",
          "py-(--footer-input-padding-y)",
          "leading-(--footer-input-line-height)",
          focusPreviewInput
            ? cn(
                "border-(--footer-input-focus-border-color)/(--footer-input-focus-border-opacity)",
                "bg-(--footer-input-focus-background-color)/(--footer-input-focus-background-opacity)",
                "text-(--footer-input-focus-foreground)/(--footer-input-focus-foreground-opacity)",
                "ring-(length:--footer-input-focus-ring-width)",
                "ring-(--footer-input-focus-ring-color)/(--footer-input-focus-ring-opacity)",
                "ring-offset-(length:--footer-input-focus-ring-offset-width)"
              )
            : cn(
                "[box-shadow:var(--footer-input-box-shadow)]",
                "focus-visible:border-(--footer-input-focus-border-color)/(--footer-input-focus-border-opacity)",
                "focus-visible:bg-(--footer-input-focus-background-color)/(--footer-input-focus-background-opacity)",
                "focus-visible:text-(--footer-input-focus-foreground)/(--footer-input-focus-foreground-opacity)",
                "focus-visible:ring-(length:--footer-input-focus-ring-width)",
                "focus-visible:ring-(--footer-input-focus-ring-color)/(--footer-input-focus-ring-opacity)",
                "focus-visible:ring-offset-(length:--footer-input-focus-ring-offset-width)"
              )
        )}
      />
      <span id={hintId} className="sr-only">
        {t("chat.footer.accessibility.input-hint")}
      </span>
    </>
  );
};
